import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CommentFilesInterface } from '../../../../interfaces/TaskInterface';
import { FILE_TYPES } from '../../../../global/constants';
import { isEmpty } from '../../../../helpers/common';
import { RawProjectFile } from '../../../../mobx/interfaces/project';
import { rootStore } from '../../../../mobx/rootStore';
import { TiptapEditorRef } from '../../../../interfaces/tiptapInterface';
import { ChatGroupInterface } from '../../../../interfaces/chatMessageInterface';
import { ProjectModel } from '../../../../mobx/models/project';

interface UseFileSelectionParams {
  currentProject: ProjectModel | ChatGroupInterface | null;
  isNavbarMessage: boolean;
  attachment: boolean;
  selectFile: boolean;
  setAttachment: (value: boolean) => void;
  setSelectFile: (value: boolean) => void;
  inlineRef: React.MutableRefObject<TiptapEditorRef | null>;
}

interface RouteParams {
  id: string;
}

interface UseFileSelectionReturn {
  attachment: boolean;
  selectFile: boolean;
  setAttachment: (value: boolean) => void;
  setSelectFile: (value: boolean) => void;
  onClickFile: (file: CommentFilesInterface) => void;
  onSelectFile: (item: RawProjectFile) => void;
}

const useFileSelection = ({
  currentProject,
  isNavbarMessage,
  attachment,
  selectFile,
  setAttachment,
  setSelectFile,
  inlineRef
}: UseFileSelectionParams): UseFileSelectionReturn => {
  const history = useHistory();
  const params = useParams<RouteParams>();

  const getFileType = useCallback((uploadFileType: number | undefined, extension: string): string => {
    if (uploadFileType === FILE_TYPES.DOCS) return 'docs';
    if (uploadFileType === FILE_TYPES.WHITEBOARD) return 'white';
    return extension;
  }, []);

  const onClickFile = useCallback(
    (file: CommentFilesInterface) => {
      const fileName = file.name.replaceAll(' ', '_');
      const chatMessageId = isNavbarMessage ? currentProject?.id : params?.id || currentProject?.id;
      if (file.href?.startsWith('/projects/files')) {
        history.push(file.href);
      } else if (
        file.href?.startsWith('https://') &&
        file?.uploadFileType !== FILE_TYPES.UPLOAD &&
        file?.isCreatedDoc
      ) {
        window.open(file.href, '_blank');
      } else if (
        file.href?.startsWith('https://') &&
        (file?.uploadFileType === FILE_TYPES.DOCS ||
          file?.uploadFileType === FILE_TYPES.DRIVE ||
          file?.uploadFileType === FILE_TYPES.DROPBOX ||
          file?.uploadFileType === FILE_TYPES.FIGMA ||
          file?.uploadFileType === FILE_TYPES.ONEDRIVE)
      ) {
        window.open(file.href, '_blank');
      } else {
        history.push(
          `/projects/details/${chatMessageId}/files/file-view/${encodeURIComponent(
            fileName
          )}?fileUrl=${encodeURIComponent(file?.href)}`
        );
      }
    },
    [currentProject?.id, history, isNavbarMessage, params?.id]
  );

  const onSelectFile = useCallback(
    (item: RawProjectFile) => {
      const parts = item?.name?.split('.');
      const extension = parts[parts.length - 1];
      let fileUrl = item?.url;
      const activeId = isNavbarMessage ? currentProject?.id : params?.id || currentProject?.id;

      if (item?.uploadFileType === FILE_TYPES.DOCS || item?.uploadFileType === FILE_TYPES.WHITEBOARD) {
        if (!isEmpty(item?.parentId) && !isEmpty(item?.id)) {
          fileUrl = `/projects/files/${activeId}/docs/${item?.id}?folder=${item?.parentId}`;
        } else if (!isEmpty(item?.id) && isEmpty(item?.parentId)) {
          fileUrl = `/projects/files/${activeId}/docs/${item?.id}`;
        }
      }

      const sizeInMB = Number(item?.size) / (1024 * 1024);
      let sizeValue: string;
      if (sizeInMB >= 1) {
        sizeValue = `${sizeInMB.toFixed(2)} MB`;
      } else {
        const sizeInKB = Number(item?.size) / 1024;
        sizeValue = `${sizeInKB.toFixed(2)} KB`;
      }

      const isImage = extension === 'png' || extension === 'jpeg' || extension === 'jpg';
      const isVideo = extension === 'mp4' || extension === 'mov' || extension === 'mkv' || extension === 'webm';
      if (isImage && inlineRef.current) {
        inlineRef.current.addImageInContent(fileUrl || '');
      } else if (isVideo && inlineRef.current) {
        inlineRef.current.addRecordingCard(fileUrl || '', item?.name);
      } else {
        const newObj: CommentFilesInterface = {
          name: item?.name,
          href: fileUrl || '',
          size: sizeValue,
          file_type: getFileType(item?.uploadFileType, extension),
          isCreatedDoc: true,
          uploadFileType: Number(item?.uploadFileType)
        };
        rootStore.richTextStore.addUploadedFiles([newObj]);
      }
    },
    [currentProject?.id, getFileType, inlineRef, isNavbarMessage, params?.id]
  );

  return {
    attachment,
    selectFile,
    setAttachment,
    setSelectFile,
    onClickFile,
    onSelectFile
  };
};

export default useFileSelection;
